import { List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import React from 'react';
import { useAsyncEffect } from '~/Hooks/useAsyncEffect';
import { BookingDialogListItem, useBookingDialogHook } from '.';
import { BORDER_COLOR, PRIMARY_BLUE, WHITE } from '../../../../Constants/Colors.constants';
import { CLOUDINARY_URLS } from '../../../../Constants/Urls.constants';
import { CustomerSortOptions } from '../../../../Helpers/Sorting.helpers';
import { getInitials } from '../../../../Helpers/String.helper';
import { addTransformationsToCloudinary } from '../../../../Helpers/Url.helpers';
import { ICustomerSimple } from '../../../../Models/DTO/Customer.model';
import { SearchCustomer } from '../../../../Models/DTO/SearchCustomer.model';
import {
  Avatar,
  Button,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '../../../../UIKit';
import { makeStyles } from '~/UIKit/Style';
import { useTranslation } from 'react-i18next';
import { CreateCustomer } from '~/Components/CustomerCreate';
import { Active } from '~/Components/Icons';

const useStyles = (screenHeight: number) =>
  makeStyles((theme) => ({
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      height: screenHeight * 0.7,
    },
    title: {
      fontSize: 22,
      lineHeight: '29px',
      fontWeight: 'bold',
      marginBottom: 27,
      marginTop: 15,
      paddingLeft: 15,
    },
    actionBar: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      borderTop: `1px solid ${BORDER_COLOR}`,
      borderBottom: `1px solid ${BORDER_COLOR}`,
      height: 42,
    },
    customerList: {
      overflow: 'auto',
      height: 'auto',
      width: '100vw',
      padding: 0,
      flexGrow: 1,

      [theme.breakpoints.up('md')]: {
        width: 775,
        maxHeight: screenHeight * 0.6,
      },
    },
    confirmBookingButton: {
      padding: 20,
      margin: '30px 15px 0',

      [theme.breakpoints.up('md')]: {
        margin: '30px 0 0',
      },
    },
    customerAvatar: {
      width: 30,
      height: 30,
    },
    select: {
      fontSize: 12,
      lineHeight: '16px',
      height: 16,
      letterSpacing: '0.5px',
      fontWeight: 'bold',
      paddingRight: 2,
      marginLeft: 15,
      marginRight: 5,
      '&:before, &:after': {
        display: 'none',
      },
    },
    searchContainer: {
      height: 40,
      borderLeft: `1px solid ${BORDER_COLOR}`,
      borderRight: `1px solid ${BORDER_COLOR}`,
      display: 'flex',
      alignContent: 'center',
      alignItems: 'center',
      flexGrow: 1,
      justifyContent: 'center',
    },
    searchField: {
      width: 224,
    },
    resultItemRoot: {
      paddingLeft: 0,
      paddingRight: 0,
      borderTop: `thin solid ${BORDER_COLOR}`,
      '&:last-child': {
        borderBottom: `thin solid ${BORDER_COLOR}`,
      },
    },
    creatableCustomer: {
      backgroundColor: PRIMARY_BLUE,
    },
    creatableCustomerName: {
      '& .MuiTypography-root': {
        color: WHITE,
      },
    },
    creatableCustomerAvatar: {
      width: 30,
      height: 30,
      backgroundColor: 'rgba(255,255,255, .3)',
    },
    addCustomerButton: {
      color: PRIMARY_BLUE,
    },
  }));

const isSelected = (selectedList: (SearchCustomer | ICustomerSimple)[]) => (
  id: number | string | undefined,
) => !!selectedList.find((item) => parseInt(`${item.id}`, 10) === parseInt(`${id || ''}`, 10));

export interface IBookingDialog {
  onClose?: () => void;
  loadEventBookings?: () => void;
}

export const BookingDialog = ({ onClose, loadEventBookings }: IBookingDialog) => {
  const { t } = useTranslation();

  const {
    customerResult,
    customers,
    selectedCustomers,
    handleSubmitBookings,
    handleCustomerSelect,
    handleSortSelect,
    bookingCreating,
    searchInput,
    closeCustomerCreationModal,
    openCustomerCreationModal,
    showCustomerCreationModal,
    setShowCustomerCreationModal,
    isAllbookingSuccess,
  } = useBookingDialogHook();
  const screenHeight = document?.documentElement.clientHeight;
  const { classes } = useStyles(screenHeight)();
  const sortOptions = CustomerSortOptions;
  const hasBeenSelected = isSelected(selectedCustomers);
  const handleSelect = (customerId: string | number | undefined = -1) => () => {
    handleCustomerSelect(customerId);
  };

  useAsyncEffect(async () => {
    if (isAllbookingSuccess && onClose) {
      onClose();
      if (loadEventBookings) {
        await loadEventBookings();
      }
    }
  }, [isAllbookingSuccess]);

  return (
    <div className={classes.wrapper}>
      <Typography className={classes.title} data-testid="BookingDialogTitle">
        {t('app.checkin.bookcustomer')}
      </Typography>
      <div className={classes.actionBar} data-testid="BookingDialogActionBar">
        <Select
          IconComponent={(props) => <KeyboardArrowDown {...props} />}
          className={classes.select}
          data-testid="TableSortSelected"
          defaultValue={sortOptions[0].type}
          onChange={(event) => handleSortSelect(event.target.value as string)}
          newDesign
        >
          {sortOptions.map((option, index) => (
            <MenuItem key={index} value={option.type}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Select>
        <IconButton
          className={classes.addCustomerButton}
          data-testid="BookingDialogDirectCreateCustomer"
          onClick={openCustomerCreationModal}
          size="large"
        >
          <Active />
        </IconButton>
      </div>
      <List className={classes.customerList} data-testid="BookingDialogCustomerList">
        {searchInput !== '' && (
          <ListItem
            button
            className={classes.creatableCustomer}
            data-testid="BookingDialogCreateCustomerItem"
            onClick={openCustomerCreationModal}
          >
            <ListItemAvatar>
              <Avatar
                alt={getInitials(searchInput, '')}
                className={classes.creatableCustomerAvatar}
                src=""
              >
                {getInitials(searchInput, '')}
              </Avatar>
            </ListItemAvatar>
            <ListItemText className={classes.creatableCustomerName}>
              {t('app.bookings.createCustomer')} <b>{searchInput}</b>
            </ListItemText>
          </ListItem>
        )}
        {selectedCustomers.map((c, index) => {
          const image = (c as ICustomerSimple).imageUrl
            ? addTransformationsToCloudinary((c as ICustomerSimple).imageUrl, ['f_auto', 'h_40'])
            : `${CLOUDINARY_URLS.AVATAR_SMALL}/${(c as SearchCustomer).image_id}`;

          return (
            <BookingDialogListItem
              firstname={c.firstname}
              handleSelect={handleSelect(c.id)}
              hasBeenSelected={hasBeenSelected}
              id={c.id}
              imageId={image}
              index={index}
              key={index}
              lastname={c.lastname}
            />
          );
        })}
        {searchInput !== '' &&
          customerResult.map((customer, index) => {
            if (hasBeenSelected(customer.id)) {
              return null;
            }
            return (
              <BookingDialogListItem
                firstname={customer.firstname}
                handleSelect={handleSelect(customer.id)}
                hasBeenSelected={hasBeenSelected}
                id={customer.id}
                imageId={`${CLOUDINARY_URLS.AVATAR_SMALL}/${customer.image_id}`}
                index={index}
                key={index}
                lastname={customer.lastname}
              />
            );
          })}
        {searchInput === '' &&
          customers.map((customer, index) => {
            if (hasBeenSelected(customer.id)) {
              return null;
            }
            return (
              <BookingDialogListItem
                firstname={customer.firstname}
                handleSelect={handleSelect(customer.id)}
                hasBeenSelected={hasBeenSelected}
                id={customer.id}
                imageId={addTransformationsToCloudinary(customer.imageUrl, ['f_auto', 'h_40'])}
                index={index}
                key={index}
                lastname={customer.lastname}
              />
            );
          })}
      </List>
      {selectedCustomers.length > 0 && (
        <Button
          className={classes.confirmBookingButton}
          color="primary"
          data-testid="BookingDialogConfirmButton"
          disabled={bookingCreating}
          onClick={handleSubmitBookings}
          variant="contained"
          isLoading={bookingCreating}
        >
          {t('app.booking.confirm')}
        </Button>
      )}

      <Dialog fullScreen onClose={closeCustomerCreationModal} open={showCustomerCreationModal}>
        <CreateCustomer
          isOpen={showCustomerCreationModal}
          setIsOpen={setShowCustomerCreationModal}
        />
      </Dialog>
    </div>
  );
};
